/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {AppContext} from '../../misc/appContext';
import BolProduct from '../../../models/bolProductModel';
import {CountryVATValues} from '../../../util/vatUtil';
import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {createErrorMessage} from '../../../util/utils';
import {onSetCrumbs} from '../../../util/events';
import Account, {ImportingStat} from '../../../models/accountModel';
import {useIdleObserver} from '../../misc/idleObserver';
import {ActivateHandle, IdleState} from '../../../util/interfaces';
import {getDefaultVats, getProductImages} from '../../../util/requestSender';
import {hasAdminLogin} from '../../../util/adminUtil';
import ProductVATController from './product/productVATController';
import VATSettings from './vatSettings';
import DefaultVATSettingsController from './defaultVat/defaultVATSettingsController';
import ImportVatController from './import/importVatController';

const SEARCH_DELAY = 500; // ms
const NUM_CHECK_DELAY = 5000; // ms
type LoadParams = { page?: number; limit?: number; search?: string; };

export default function VATSettingsController() {
    const context = useContext(AppContext), {account} = context.accountContext;
    const [products, setProducts] = useState<Array<BolProduct>>([]);
    const [loading, setLoading] = useState(false);
    const [importingProducts, setImportingProducts] = useState(false);
    const [importingCount, setImportingCount] = useState<number | null>(null);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [defaultVATs, setDefaultVATs] = useState<CountryVATValues>(new CountryVATValues());
    const countTimeout = useRef<number | undefined>(undefined);
    const searchTimeout = useRef<number | undefined>(undefined);
    const uploadRef = useRef<ActivateHandle>(null);
    const productRef = useRef<ActivateHandle>(null);
    const defaultVATsRef = useRef<ActivateHandle>(null);
    useIdleObserver(onIdleStateChange);

    function onIdleStateChange(currentState: IdleState) {
        if (currentState === IdleState.IDLE) clearCountCheck();
        else checkImportingCount();
    }

    function onChangeSearch(value: string) {
        clearSearchTimeout();
        setSearch(value);
        searchTimeout.current = window.setTimeout(() => {
            setPage(0);
            loadProducts({search: value, page: 0});
        }, SEARCH_DELAY);
    }

    function onEnterSearch() {
        clearSearchTimeout();
        loadProducts();
    }

    function onClickSync() {
        if (!account) return;
        setLoading(true);
        BolProduct.importAsync(account.id, false, (_: Account | null, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                checkImportingCount();
                showSnackbar('De producten worden opgehaald');
            } else showDialog(createErrorMessage(data.message ?? 'Het ophalen van de producten is niet gelukt.'),
                'Ophalen mislukt');
        });
    }

    function onClickBulkUpload() {
        uploadRef.current?.activate();
    }

    function onClickProduct(product: BolProduct) {
        return function () {
            productRef.current?.activate(product, defaultVATs); // TODO
        };
    }

    function onClickDefault() {
        defaultVATsRef.current?.activate(defaultVATs);
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            loadProducts({page: nextPage});
        };
    }

    function onChangeLimit(limit: number) {
        setLimit(limit);
        loadProducts({limit: limit});
    }

    function onDefaultVATChanged(values: CountryVATValues) {
        setDefaultVATs(values);
    }

    function onProductVATChanged(product: BolProduct) {
        for (let ix = 0; ix < products.length; ix++) {
            if (products[ix].id === product.id) {
                products[ix].title = product.title;
                products[ix].vats = product.vats;
                setProducts(products.slice());
                return;
            }
        }
    }

    function checkImportingCount() {
        if (!account) return;
        account.getImportingProductsCount().then((data: ImportingStat) => {
            if (data.importing) countTimeout.current = window.setTimeout(checkImportingCount, NUM_CHECK_DELAY);
            else if (importingProducts) loadProducts();
            setImportingCount(data.products);
            setImportingProducts(data.importing);
        });
    }

    function loadProducts(params?: LoadParams) {
        if (!account) return;
        const _page = params?.page ?? page;
        const _limit = params?.limit ?? limit;
        const _search = params?.search ?? search;
        setLoading(true);
        BolProduct.getAll(
            {accountId: account.id, page: _page, limit: _limit, search: _search, sortCustomVAT: true, withImages: true},
            (products: Array<BolProduct>, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setProducts(products);
                    loadImages(products);
                    if (_page === 0) setTotal(data.total);
                } else showDialog(createErrorMessage(data.message ?? 'Het laden van de producten is niet gelukt.'),
                    'Laden mislukt');
            });
    }

    function loadImages(products: Array<BolProduct>) {
        if (!account) return;
        const eans = products.filter((prod: BolProduct) => !prod.imageURL).map((product: BolProduct) => product.ean);
        if (!eans.length) return;
        getProductImages({
            data: {account_id: account.id, eans}, callback: (data: ResponseData) => {
                if (!data.success) return;
                const images: Record<string, string> = data.images;
                const _products = products.slice();
                for (const [ean, imageURL] of Object.entries(images)) {
                    const product = _products.find((prod: BolProduct) => prod.ean === ean);
                    if (!product) continue;
                    product.imageURL = imageURL;
                }
                setProducts(_products);
            }
        });
    }

    function loadDefaultVats() {
        if (!account) return;
        getDefaultVats({
            account_id: account.id, callback: (data: ResponseData) => {
                if (data.success) setDefaultVATs(new CountryVATValues(data.default_vats));
            }
        });
    }

    function saveAccountSetupProgress() {
        if (hasAdminLogin()) return;
        if (!account || account.setupProgress.vat) return;
        account.setupProgress.vat = true;
        account.update(undefined, undefined, undefined, (account: Account, success: boolean) => {
            if (success) context.onChangeContext({account});
        });
    }

    function clearCountCheck() {
        window.clearTimeout(countTimeout.current);
        countTimeout.current = undefined;
    }

    function clearSearchTimeout() {
        window.clearTimeout(searchTimeout.current);
        searchTimeout.current = undefined;
    }

    useEffect(() => {
        loadProducts();
        loadDefaultVats();
        checkImportingCount();
        saveAccountSetupProgress();

        return function () {
            clearCountCheck();
            clearSearchTimeout();
        };
    }, [account?.id]);

    useEffect(() => {
        onSetCrumbs([{label: 'Instellingen'}, {label: 'Producten & BTW'}]);

        return function () {
            clearCountCheck();
            clearSearchTimeout();
        };
    }, []);

    return (
        <>
            <VATSettings onClickSync={onClickSync} onClickBulkUpload={onClickBulkUpload} onClickProduct={onClickProduct}
                         onClickDefault={onClickDefault} onChangeSearch={onChangeSearch} onEnterSearch={onEnterSearch}
                         onChangePage={onChangePage} onChangeLimit={onChangeLimit} products={products} total={total}
                         search={search} defaultVATs={defaultVATs} page={page} limit={limit}
                         isImporting={importingProducts} importingCount={importingCount} loading={loading}/>
            <DefaultVATSettingsController onSuccess={onDefaultVATChanged} ref={defaultVATsRef}/>
            <ProductVATController onSuccess={onProductVATChanged} ref={productRef}/>
            <ImportVatController ref={uploadRef} onSuccess={loadProducts}/>
        </>
    );
}